import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, RotateCcw, Bot } from 'lucide-react';
import { chatbotAPI } from '../api';
import './chatbot.css';

const SUGGESTIONS = [
  'Does it work without internet?',
  'How much does it cost?',
  'How many warehouses can I manage?',
  'Can I try it for free?',
  'How do I migrate my data?',
  'How do I get started?',
];

const GREETING = "Hi, I'm Taalu 👋 Ask me anything about TaalumaERP — features, pricing, how it works, who it's for.";

// Nudge appears after this many ms on page, auto-hides after NUDGE_HIDE ms
const NUDGE_DELAY   = 8000;   // initial appearance — after 8s on page
const NUDGE_HIDE    = 12000;  // auto-dismiss after 12s
const IDLE_TIMEOUT  = 60000;  // reappear after 60s of no interaction
const IDLE_EVENTS   = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

export default function ChatBot({ theme }) {
  const [open, setOpen]           = useState(false);
  const [messages, setMessages]   = useState([{ role: 'assistant', content: GREETING }]);
  const [input, setInput]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [nudge, setNudge]  = useState(false);
  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);
  const nudgeTimer = useRef(null);
  const hideTimer  = useRef(null);
  const idleTimer  = useRef(null);

  function showNudge() {
    setNudge(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setNudge(false), NUDGE_HIDE);
  }

  function resetIdle() {
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      // Only show if chat is closed and nudge isn't already visible
      setNudge(prev => {
        if (!prev) showNudge();
        return prev;
      });
    }, IDLE_TIMEOUT);
  }

  // Initial nudge after page load
  useEffect(() => {
    nudgeTimer.current = setTimeout(showNudge, NUDGE_DELAY);
    return () => {
      clearTimeout(nudgeTimer.current);
      clearTimeout(hideTimer.current);
      clearTimeout(idleTimer.current);
    };
  }, []);

  // Idle detection — reset timer on any user activity
  useEffect(() => {
    // Start idle timer immediately
    resetIdle();
    // Reset on every interaction
    IDLE_EVENTS.forEach(e => window.addEventListener(e, resetIdle, { passive: true }));
    return () => {
      IDLE_EVENTS.forEach(e => window.removeEventListener(e, resetIdle));
      clearTimeout(idleTimer.current);
    };
  }, []);

  // Dismiss nudge when chat opens
  useEffect(() => {
    if (open) {
      setNudge(false);
      clearTimeout(hideTimer.current);
      clearTimeout(idleTimer.current); // pause idle while chat is open
      setTimeout(() => inputRef.current?.focus(), 150);
    } else {
      // Restart idle detection when chat closes
      resetIdle();
    }
  }, [open]);

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  function dismissNudge() {
    setNudge(false);
    clearTimeout(hideTimer.current);
    // Restart idle timer so nudge can reappear after next idle period
    resetIdle();
  }

  function openFromNudge() {
    dismissNudge();
    setOpen(true);
  }

  async function send(text) {
    const userText = (text || input).trim();
    if (!userText || loading) return;

    setInput('');
    setShowSuggestions(false);
    setMessages(prev => [...prev, { role: 'user', content: userText }]);
    setLoading(true);

    try {
      const response = await chatbotAPI.sendMessage(userText);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.answer || "I didn't catch that — try rephrasing.",
      }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm having trouble right now. Reach us at support@taalumaerp.com",
      }]);
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setMessages([{ role: 'assistant', content: GREETING }]);
    setInput('');
    setShowSuggestions(true);
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  }

  return (
    <>
      {/* ── CHAT WINDOW ── */}
      <div className={`cb-window ${open ? 'cb-window--open' : ''}`} data-theme={theme}>
        <div className="cb-header">
          <div className="cb-header__brand">
            <div className="cb-avatar">
              <Bot size={16}/>
              <span className="cb-avatar__dot"></span>
            </div>
            <div>
              <div className="cb-header__name">Taalu</div>
              <div className="cb-header__sub">TaalumaERP assistant</div>
            </div>
          </div>
          <div className="cb-header__actions">
            <button className="cb-icon-btn" onClick={reset} title="Restart"><RotateCcw size={14}/></button>
            <button className="cb-icon-btn" onClick={() => setOpen(false)} title="Close"><X size={16}/></button>
          </div>
        </div>

        <div className="cb-messages">
          {messages.map((m, i) => (
            <div key={i} className={`cb-msg cb-msg--${m.role}`}>
              {m.role === 'assistant' && <div className="cb-msg__avatar"><Bot size={13}/></div>}
              <div className="cb-msg__bubble">{m.content}</div>
            </div>
          ))}

          {showSuggestions && messages.length === 1 && (
            <div className="cb-suggestions">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} className="cb-suggestion" onClick={() => send(s)}>{s}</button>
              ))}
            </div>
          )}

          {loading && (
            <div className="cb-msg cb-msg--assistant">
              <div className="cb-msg__avatar"><Bot size={13}/></div>
              <div className="cb-msg__bubble cb-msg__bubble--typing">
                <span/><span/><span/>
              </div>
            </div>
          )}
          <div ref={bottomRef}/>
        </div>

        <div className="cb-input-wrap">
          <input
            ref={inputRef}
            className="cb-input"
            type="text"
            placeholder="Ask about TaalumaERP…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            disabled={loading}
          />
          <button className="cb-send" onClick={() => send()} disabled={!input.trim() || loading}>
            <Send size={15}/>
          </button>
        </div>
        <div className="cb-footer">Only answers questions about TaalumaERP</div>
      </div>

      {/* ── NUDGE BUBBLE — appears after 8s, slides in above the trigger ── */}
      {nudge && !open && (
        <div className="cb-nudge" data-theme={theme}>
          <button className="cb-nudge__dismiss" onClick={dismissNudge}><X size={12}/></button>
          <div className="cb-nudge__avatar"><Bot size={14}/></div>
          <div className="cb-nudge__text">
            <strong>Got questions?</strong>
            <span>I can help with pricing, features, and more.</span>
          </div>
          <button className="cb-nudge__cta" onClick={openFromNudge}>Ask Taalu →</button>
        </div>
      )}

      {/* ── TRIGGER BUTTON ── */}
      <button
        className={`cb-trigger ${open ? 'cb-trigger--open' : ''}`}
        onClick={() => setOpen(o => !o)}
        data-theme={theme}
      >
        {open
          ? <X size={22}/>
          : <><MessageCircle size={22}/><span className="cb-trigger__label">Ask Taalu</span></>
        }
        {!open && <span className="cb-trigger__ping"></span>}
      </button>
    </>
  );
}